import { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

type Props = {
  open: boolean
  initialLat: number | null
  initialLng: number | null
  onClose: () => void
  onConfirm: (lat: number, lng: number) => void
}

const DEFAULT_CENTER: [number, number] = [39.5, -98.35]

function fixed(value: number): string {
  return value.toFixed(6)
}

export default function LocationPickerModal({ open, initialLat, initialLng, onClose, onConfirm }: Props) {
  const mapElRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markerRef = useRef<L.Marker | null>(null)
  const [selected, setSelected] = useState<{ lat: number; lng: number }>(() => ({
    lat: initialLat ?? DEFAULT_CENTER[0],
    lng: initialLng ?? DEFAULT_CENTER[1],
  }))

  const startCenter = useMemo<[number, number]>(() => {
    if (initialLat != null && initialLng != null) return [initialLat, initialLng]
    return DEFAULT_CENTER
  }, [initialLat, initialLng])

  useEffect(() => {
    if (!open) return
    setSelected({
      lat: initialLat ?? DEFAULT_CENTER[0],
      lng: initialLng ?? DEFAULT_CENTER[1],
    })
  }, [open, initialLat, initialLng])

  useEffect(() => {
    if (!open || !mapElRef.current || mapRef.current) return

    const map = L.map(mapElRef.current, {
      center: startCenter,
      zoom: initialLat != null && initialLng != null ? 13 : 4,
      zoomControl: true,
      // Make mouse-wheel zoom less jumpy on high-sensitivity wheels/trackpads.
      zoomSnap: 0.25,
      zoomDelta: 0.25,
      wheelPxPerZoomLevel: 240,
      wheelDebounceTime: 80,
    })
    mapRef.current = map

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map)

    const pinIcon = L.divIcon({
      className: '',
      html: '<div style="width:20px;height:20px;border-radius:9999px;background:#2563eb;border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.45);"></div>',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    })

    const marker = L.marker(startCenter, { draggable: true, icon: pinIcon }).addTo(map)
    markerRef.current = marker

    marker.on('dragend', () => {
      const pos = marker.getLatLng()
      setSelected({ lat: pos.lat, lng: pos.lng })
    })

    map.on('click', (e: L.LeafletMouseEvent) => {
      const lat = e.latlng.lat
      const lng = e.latlng.lng
      marker.setLatLng([lat, lng])
      setSelected({ lat, lng })
    })

    setTimeout(() => map.invalidateSize(), 0)

    return () => {
      marker.off()
      map.off()
      map.remove()
      markerRef.current = null
      mapRef.current = null
    }
  }, [open, startCenter, initialLat, initialLng])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-3xl rounded-2xl border border-white/10 bg-[#111318] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="text-base font-semibold">Pick location on map</div>
          <button type="button" onClick={onClose} className="rounded-lg border border-white/15 px-2.5 py-1.5 text-sm text-white/80 hover:bg-white/10">
            Close
          </button>
        </div>
        <div className="p-4">
          <div ref={mapElRef} className="h-[320px] w-full overflow-hidden rounded-xl border border-white/10" />
          <div className="mt-3 text-sm text-white/80">
            Selected: {fixed(selected.lat)}, {fixed(selected.lng)}
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(selected.lat, selected.lng)}
              className="rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600"
            >
              Use this location
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
